<template>
  <div class="event-info-edits">
    <div class="">
      <v-row :no-gutters="$vuetify.display.lgAndUp" class="mx-0">
        <v-col v-if="!isNoncompetition" cols="12" sm="8">
          <slot name="label" />
          <AppShowEventApprovalLogs :event="event" />
        </v-col>
        <v-col
          v-if="canEdit"
          cols="12"
          :sm="isNoncompetition ? 12 : 4"
          class="text-center text-sm-right"
        >
          <v-btn
            v-edit-vertical-line="{
              targetSelector: '.event-info-edits',
              color: 'green'
            }"
            v-bind="{
              width: $vuetify.display.xs ? '295px' : undefined,
              height: $vuetify.display.xs ? '40px' : undefined
            }"
            color="edit"
            data-test="provisionsEditMode"
            @click="
              () => {
                editmode = !editmode;
              }
            "
          >
            <template v-if="editmode">
              {{ $t("common:button.back", "Back", "Back button") }}
            </template>
            <template v-else>
              <v-icon color="white" size="small" start> $edit </v-icon>
              {{ $t("common:button.edit", "Edit") }}</template
            >
          </v-btn>
        </v-col>
      </v-row>

      <div
        v-for="eventInfoCategory in availableInfoCategories"
        :key="eventInfoCategory.id"
        :data-test="eventInfoCategory.name"
        class="mt-3"
      >
        <v-card flat>
          <v-card-title
            class="d-flex justify-space-between flex-column flex-sm-row"
          >
            <div style="font-size: 18px" class="text-center text-sm-start">
              <span
                :style="
                  eventInfoInCategory(eventInfoCategory.id)
                    ? 'font-weight: bold;'
                    : ''
                "
                >{{ eventInfoCategory.name }}</span
              >
            </div>

            <div v-if="eventInfoInCategory(eventInfoCategory.id) && editmode">
              <v-btn-icon
                icon
                size="small"
                class="bg-edit-green ma-2"
                data-test="provisionsEdit"
                @click="editItem(eventInfoCategory.id)"
              >
                <v-icon size="small" color="white"> $edit </v-icon>
              </v-btn-icon>
              <v-btn-icon
                icon
                size="small"
                class="bg-remove"
                data-test="provisionsDelete"
                @click="
                  deleteItem(
                    eventInfoInCategory(eventInfoCategory.id).id,
                    $event
                  )
                "
              >
                <v-icon size="small" color="white"> $iconTrash </v-icon>
                <v-progress-circular
                  v-show="false"
                  color="white"
                  small
                  indeterminate
                  data-test="deletion"
                />
              </v-btn-icon>
            </div>
            <div v-else-if="editmode" class="text-center text-sm-end">
              <v-btn
                v-bind="{
                  width: $vuetify.display.xs ? '295px' : undefined,
                  height: $vuetify.display.xs ? '40px' : undefined
                }"
                :class="{
                  'ma-2': $vuetify.display.xs
                }"
                color="edit"
                dark
                data-test="provisionsAdd"
                @click="editItem(eventInfoCategory.id)"
              >
                <v-icon size="small" class="text-white mr-2">
                  $iconPlus
                </v-icon>
                {{ $t("common:button.add", "Add", "Add button") }}
              </v-btn>
            </div>
          </v-card-title>
          <template v-if="editingCategoryId == eventInfoCategory.id">
            <v-col cols="12">
              <AppRichTextField
                v-model="form.text"
                data-test="provisionTextField"
                apply-height-limit
              />
            </v-col>
            <v-col cols="12" md="8">
              <AppBtnsRow>
                <template #left="{ attributes }">
                  <v-btn
                    v-bind="attributes"
                    data-test="provisionCancel"
                    @click.stop="handleCancel"
                  >
                    {{ $t("common:button.cancel", "Cancel", "Cancel button") }}
                  </v-btn>
                </template>
                <template #right="{ attributes }">
                  <v-btn
                    v-bind="attributes"
                    color="accent"
                    data-test="provisionSave"
                    :loading="formSubmited"
                    @click="handleSubmit"
                  >
                    {{ $t("common:button.save", "Save", "Save button") }}
                  </v-btn>
                </template>
              </AppBtnsRow>
            </v-col>
          </template>
          <template v-else>
            <article
              v-if="eventInfoInCategory(eventInfoCategory.id)"
              data-test="provisionText"
              class="pb-1 provision-paragraphs"
              style="padding: 16px; padding-top: 0"
              v-html="eventInfoInCategory(eventInfoCategory.id).text"
            />
            <v-card-text v-else>
              {{ eventInfoCategory.description }}
            </v-card-text>
          </template>
          <v-divider style="border-color: grey" />
        </v-card>
      </div>
    </div>
  </div>
</template>

<script>
import gql from "fraql";
import { useEventInfoCategoryHelper } from "@composables/masterdata/eventInfoCategories";
import editVerticalLine from "@/directives/edit-vertical-line.js";
import { useSaveMeta } from "@/composables/form/save_meta";

import AppRichTextField from "@/components/elements/AppRichTextField.vue";
import AppShowEventApprovalLogs from "@AppShows/event/AppShowEventApprovalLogs.vue";
import AppBtnsRow from "@elements/globals/AppBtnsRow.vue";
import { showLoadingButton } from "@/utils/showLoadingButton";
export default {
  name: "EventInfoFields",
  components: { AppRichTextField, AppShowEventApprovalLogs, AppBtnsRow },
  directives: { editVerticalLine },
  inject: {
    isNoncompetition: {
      default: false
    }
  },
  props: {
    event: {
      type: Object,
      required: true
    }
  },
  setup() {
    const { saveMetaSetOriginalForm, saveMetaConfirmUnsaveChanges } =
      useSaveMeta(null, { useOptionsAPIForm: true });
    const { masterEventInfoCategories, getEventInfoCategoryFromMaster } =
      useEventInfoCategoryHelper();
    return {
      masterEventInfoCategories,
      getEventInfoCategoryFromMaster,
      saveMetaSetOriginalForm,
      saveMetaConfirmUnsaveChanges
    };
  },
  data() {
    return {
      form: {
        id: null,
        text: "",
        eventInfoCategoryId: null,
        eventId: this.eventId
      },
      editingCategoryId: null,
      editmode: false,
      formSubmited: false
    };
  },
  computed: {
    canEdit() {
      return this.$allowedMutation(
        this.event.allowedMutations,
        "eventInfoFieldCreateOrUpdate"
      );
    },
    availableInfoCategories() {
      return this.masterEventInfoCategories.filter(infoCategory => {
        if (
          this.editmode &&
          this.event.suitableEventInfoCategoryIds.includes(infoCategory.id)
        ) {
          return true;
        }
        return this.event.infoFields.some(
          infoField => infoField.eventInfoCategoryId === infoCategory.id
        );
      });
    }
  },
  watch: {
    async editmode(newVal) {
      if (newVal === false) {
        if ((await this.handleCancel("editmode")) === true) {
          this.editmode = false;
        } else {
          this.editmode = true;
        }
      }
    }
  },
  fragments: {
    infoFields: gql`
      fragment _ on Event {
        ${AppShowEventApprovalLogs.fragments.eventApprovalLogs}
        suitableEventInfoCategoryIds
        infoFields {
          id
          eventInfoCategoryId
          eventInfoCategory @client {
            id
            name
            description
            rank
            template
          }
          categoryName
          text
        }
      }
    `
  },
  methods: {
    eventInfoInCategory(categoryId) {
      return this.event.infoFields.find(
        item => item.eventInfoCategoryId == categoryId
      );
    },
    clearForm() {
      this.form = {
        id: null,
        text: "",
        eventInfoCategoryId: null,
        eventId: this.event.id
      };
      this.editingCategoryId = null;
      this.saveMetaSetOriginalForm(this.form);
    },
    async editItem(eventInfoCategoryId) {
      const saveMetaAnswer = await this.saveMetaConfirmUnsaveChanges();
      if (!this.form.eventInfoCategoryId || saveMetaAnswer === true) {
        this.form = Object.assign(
          { eventId: this.event.id },
          this.eventInfoInCategory(eventInfoCategoryId)
        );
        if (!this.form.id) {
          this.form = {
            id: null,
            text: "",
            eventInfoCategoryId: eventInfoCategoryId,
            eventId: this.event.id
          };
        }
        if (this.form.text.length == 0) {
          this.form.text = this.getEventInfoCategoryFromMaster(
            eventInfoCategoryId,
            "template"
          );
        }
        this.editingCategoryId = eventInfoCategoryId;
        this.saveMetaSetOriginalForm(this.form);
      }
    },
    async handleCancel(condition) {
      const response = await this.saveMetaConfirmUnsaveChanges();
      if (response === true) {
        this.clearForm();
        if (condition == "editmode") {
          return true;
        }
      }
    },
    async handleSubmit() {
      this.formSubmited = true;
      if (!this.form.text) this.form.text = null;

      try {
        await this.$apollo.mutate({
          mutation: gql`
            mutation eventInfoFieldCreateOrUpdate(
              $id: ID
              $eventId: ID!
              $eventInfoCategoryId: ID!
              $text: String!
            ) {
              eventInfoFieldCreateOrUpdate(
                input: {
                  id: $id
                  eventId: $eventId
                  eventInfoCategoryId: $eventInfoCategoryId
                  text: $text
                }
              ) {
                event {
                  id
                  infoFields {
                    id
                    eventInfoCategoryId
                    categoryName
                    text
                  }
                }
              }
            }
          `,
          variables: this.form
        });

        this.clearForm();
        this.$root.$snackbar("success");
      } catch (error) {
        this.$root.$snackbarServerError(error, { level: "error" });
      } finally {
        this.formSubmited = false;
      }
    },
    deleteItem(ID, node) {
      this.$root
        .$confirm(
          this.$t("common:confirmation.delete", "Delete product"),
          this.$t("common:confirmation.areYouSure", "Are you sure?"),
          { color: "red" }
        )
        .then(async confirm => {
          if (confirm) {
            const removeLoading = showLoadingButton(node.target);
            this.$apollo
              .mutate({
                mutation: gql`
                  mutation eventInfoFieldDelete($id: ID!) {
                    eventInfoFieldDelete(input: { id: $id }) {
                      event {
                        id
                        infoFields {
                          id
                          categoryName
                          eventId
                          text
                        }
                      }
                    }
                  }
                `,
                variables: { id: ID },
                refetchQueries: [
                  {
                    query: gql`
                      query event($id: ID) {
                        event(id: $id) {
                          id
                          ${this.$options.fragments.infoFields}
                        }
                      }
                    `,
                    variables: { id: this.event.id }
                  }
                ]
              })
              .then(() => {
                this.$root.$snackbar("success", {
                  text: this.$t(
                    "common:feedbackMessage.removedSuccessfully.text",
                    "Succesfully deleted!"
                  )
                });
              })
              .catch(error => {
                // Error
                removeLoading();
                this.$root.$snackbarServerError(error, { level: "error" });
              });
          }
        });
    }
  }
};
</script>
<style>
.entsys--card-info > div {
  transition: all 0.25s;
}
.entsys--card-info > div:hover {
  padding-left: 25px;
}

.provision-paragraphs p {
  margin-bottom: 4px;
}
</style>
